import * as React from "react";
import { useState, useEffect } from "react";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import PersonAdd from "@mui/icons-material/PersonAdd";
import Container from "@mui/material/Container";
import intl from "react-intl-universal";
import { Form, useLocation, useNavigate } from "react-router-dom";
import Copyright from "../components/Copyright";
import { Routes } from "../Routes";
import { Alert, Typography } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import UserService from "../services/UserService";
import MaskedTextField from "../components/MaskedTextField";



export default function CreateAccount() {
  const location = useLocation();
  const { state } = location;

  const [dob, setDob] = useState(null);
  const [permitExpiration, setPermitExpiration] = useState(null);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const handleSubmit = async (event) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    formData.set("dob", dob ? dob.format("MM/DD/YYYY") : "");
    formData.set(
      "permitExpiration",
      permitExpiration ? permitExpiration.format("MM/DD/YYYY") : ""
    );
    formData.set("email", state.email);
    formData.set("phone", state.phone);
    formData.set("password", state.password);
    formData.set("companyName", state.companyName);
    formData.set("userName", state.email);

    const userData = Object.fromEntries(formData.entries());
    try {
      const response = await UserService.createUser(userData);
      if (response.ok) {
		const successMessage = intl
                    .get("accountsuccessfullycreated")
                    .d("Account successfully created.");
        navigate(Routes.signIn + (state.companyName ? "/" + state.companyName : ""), { state: { message: successMessage, email: state.email} });
      } else {
        // set the error message from response.error
        const errorMessage = response.error || "Error creating user.";
        setError(errorMessage);
      }
    } catch (err) {
      setError("An unexpected error occurred. Please try again.");
    }
  };


  useEffect(() => {
    if (!state?.email) {
      navigate(Routes.signUp, { state: {} });
    }
  }, [state, navigate]);


  return (
    <Container component="main" maxWidth="xs">
      <Box
          sx={{
            marginTop: 5,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
      >
        <Avatar sx={{ m: 1, bgcolor: "primary.main" }}>
          <PersonAdd />
        </Avatar>

        <Typography component="h1" variant="h5">
          {intl.get("accountinformation").d("Account Information")}
        </Typography>

		<Box sx={{ mt: 1 }}>
          <Form method="post" onSubmit={handleSubmit} sx={{ mt: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} >
                <Typography component="p" color="textPrimary" >
                  {intl
                    .get("thankyouforverifyingyouridentity")
                    .d("Thank you for verifying your identity.")}
                </Typography>

                <Typography component="p" color="textSecondary">
                  {intl
                    .get("completetheinformationbelow...")
                    .d(
                      "Complete the information below to finish setting up your account."
                    )}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  id="fname"
                  name="fname"
                  required
                  fullWidth
                  label={intl.get("firstname").d("First name")}
                  autoComplete="given-name"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  id="lname"
                  name="lname"
                  label={intl.get("lastname").d("Last name")}
                  autoComplete="family-name"
                />
              </Grid>
              <Grid item xs={12}>
                <DatePicker
                  disableFuture
                  label={intl.get("dob").d("Date of birth")}
                  value={dob}
                  onChange={(newValue) => setDob(newValue)}
                  format="MM/DD/YYYY"
                  slotProps={{
                    textField: {
                      variant: "outlined",
                      required: true,
                      fullWidth: true,
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <MaskedTextField
                  mask="999-999-9999"
                  alwaysShowMask={false}
                  required
                  fullWidth
                  id="permitId"
                  name="permitId"
                  label={intl.get("permitnumber").d("Permit Number")}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DatePicker
                  disablePast
                  label={intl.get("permitexpiration").d("Permit Expiration")}
                  value={permitExpiration}
                  onChange={(newValue) => setPermitExpiration(newValue)}
                  format="MM/DD/YYYY"
                  slotProps={{
                    textField: {
                      variant: "outlined",
                      required: true,
                      fullWidth: true,
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  id="address1"
                  name="address1"
                  label={intl.get("addressline1").d("Address line 1")}
                  autoComplete="address-line1"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  id="address2"
                  name="address2"
                  label={intl.get("addressline2").d("Address line 2")}
                  autoComplete="address-line2"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  required
                  fullWidth
                  id="city"
                  name="city"
                  label={intl.get("city").d("City")}
                  autoComplete="address-level2"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  required
                  fullWidth
                  id="state"
                  name="state"
                  label={intl.get("state").d("State")}
                  autoComplete="address-level1"
                  inputProps={{ maxLength: 2 }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  required
                  fullWidth
                  id="zipcode"
                  name="zipcode"
                  label={intl.get("zip").d("Zip")}
                  autoComplete="postal-code"
                />
              </Grid>

               {error && (
                <Grid item xs={12}>
                  <Alert severity="error">{error}</Alert>
                </Grid>
              )}   
              
            </Grid>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
            >
              {intl.get("createaccount").d("Create account")}
            </Button>
          </Form>
        </Box>
      </Box>
      <Copyright sx={{ mt: 5 }} />
    </Container>
  );
}
